// Native background AVL: while on duty, report the device GPS to CAD.
//
// Uses @capacitor-community/background-geolocation, which runs an Android
// foreground service so location keeps flowing when the app is backgrounded or
// the screen is off. Each fix is POSTed to /mobile/gps, which maps it to the
// logged-in personnel's unit and ignores it if the unit is off duty — so the
// server is the source of truth for "on duty" and we never track after logoff.
import { registerPlugin } from '@capacitor/core';
import { isNative, getApiBase } from './tenant';

interface Location {
  latitude: number;
  longitude: number;
  speed: number | null;
  bearing: number | null;
}

interface BackgroundGeolocationPlugin {
  addWatcher(
    options: {
      backgroundMessage?: string;
      backgroundTitle?: string;
      requestPermissions?: boolean;
      stale?: boolean;
      distanceFilter?: number;
    },
    callback: (position?: Location, error?: { code?: string; message?: string }) => void,
  ): Promise<string>;
  removeWatcher(options: { id: string }): Promise<void>;
}

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

let watcherId: string | null = null;

async function report(loc: Location) {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    await fetch(`${getApiBase()}/api/mobile/gps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        lat: loc.latitude,
        lng: loc.longitude,
        speed: loc.speed,
        heading: loc.bearing,
      }),
    });
  } catch {
    // A dropped fix is fine — the next one will report the current position.
  }
}

export async function startAvl(): Promise<void> {
  if (!isNative() || watcherId) return;
  try {
    watcherId = await BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: 'Reporting your location to dispatch while on duty.',
        backgroundTitle: 'WatchCenter MDT — On Duty',
        requestPermissions: true,
        stale: false,
        distanceFilter: 15,
      },
      (position, error) => {
        if (error || !position) return;
        void report(position);
      },
    );
  } catch {
    watcherId = null;
  }
}

export async function stopAvl(): Promise<void> {
  if (!watcherId) return;
  const id = watcherId;
  watcherId = null;
  try {
    await BackgroundGeolocation.removeWatcher({ id });
  } catch {
    // ignore
  }
}
